import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    FetchInventoryActivityLogsInput,
    InventoryActivityLogChange,
    InventoryActivityLogEventType,
    InventoryActivityLogResponse,
} from '../../types/inventory';
import {
    EMPTY_ACTIVITY_FEED_FILTERS,
    activityFeedFiltersToApiParams,
    type ActivityFeedApiFilterParams,
    type ActivityFeedFiltersValue,
} from '../../components/activityFeedFilters';

const getInventoryActivityLogs = async (
    sessionId: string,
    input: FetchInventoryActivityLogsInput,
): Promise<InventoryActivityLogResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/activity-logs`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

function toInventoryFetchInput(
    base: Omit<FetchInventoryActivityLogsInput, 'eventTypes'>,
    filters: ActivityFeedApiFilterParams,
): FetchInventoryActivityLogsInput {
    const { eventTypes, ...restFilters } = filters;
    return {
        ...base,
        ...restFilters,
        ...(eventTypes?.length
            ? { eventTypes: eventTypes as InventoryActivityLogEventType[] }
            : {}),
    };
}

export const useGetInventoryActivityLogs = (
    inventoryItemId: string | undefined,
    page = 1,
    limit = 50,
    enabled = true,
    search = '',
    filters: ActivityFeedFiltersValue = EMPTY_ACTIVITY_FEED_FILTERS,
) => {
    const sessionContext = useContext(SessionContext);
    const trimmedSearch = search.trim();

    return useQuery({
        queryKey: ['/inventory/activity-logs', inventoryItemId, page, limit, trimmedSearch, filters],
        queryFn: () => getInventoryActivityLogs(
            sessionContext?.sessionId ?? '',
            toInventoryFetchInput({
                inventoryItemId: inventoryItemId!,
                page,
                limit,
                ...(trimmedSearch ? { search: trimmedSearch } : {}),
            }, activityFeedFiltersToApiParams(filters)),
        ),
        enabled: !!sessionContext?.sessionId && !!inventoryItemId && enabled,
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: (prev, prevQuery) =>
            prevQuery?.queryKey[1] === inventoryItemId ? prev : undefined,
    });
};

export const useGetAllInventoryActivityLogs = (
    page: number,
    limit = 50,
    enabled = true,
    search = '',
    filters: ActivityFeedFiltersValue = EMPTY_ACTIVITY_FEED_FILTERS,
) => {
    const sessionContext = useContext(SessionContext);
    const trimmedSearch = search.trim();

    return useQuery({
        queryKey: ['/inventory/activity-logs', 'all', page, limit, trimmedSearch, filters],
        queryFn: () => getInventoryActivityLogs(
            sessionContext?.sessionId ?? '',
            toInventoryFetchInput({
                all: true,
                page,
                limit,
                ...(trimmedSearch ? { search: trimmedSearch } : {}),
            }, activityFeedFiltersToApiParams(filters)),
        ),
        enabled: !!sessionContext?.sessionId && enabled,
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: (prev, prevQuery) =>
            prevQuery?.queryKey[1] === 'all' ? prev : undefined,
    });
};

export const parseInventoryActivityChanges = (
    metadata: Record<string, string> | undefined,
): InventoryActivityLogChange[] => {
    if (!metadata?.changes) return [];
    try {
        const parsed = JSON.parse(metadata.changes);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};
